/**
 * Analyzer: Day First-Trade Effect
 *
 * First trade + rest-of-day dynamic. Four cases:
 *   - Won first AND rest positive  → Strong Start
 *   - Lost first BUT rest positive → Recovery
 *   - Won first BUT rest negative  → Gave Back Profits (warning)
 *   - Lost first AND rest negative → Spiraling (warning)
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayFirstTradeEffect';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.time || !day.time.firstTrade || !day.time.restOfDay) return null;

  const first = day.time.firstTrade;
  const rest = day.time.restOfDay;
  if (rest.trades < 1) return null;

  const firstWon = first.wasWinner;
  const restPnL = rest.pnl;

  // Won first, rest positive → clean momentum
  if (firstWon && restPnL > 0) {
    return makeObservation({
      id: `${key}:${day.date}:momentum`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.EMOTIONAL,
      title: 'Won first, kept building',
      detail: `First trade +$${first.pnl.toFixed(0)} and the next ${rest.trades} added +$${restPnL.toFixed(0)} (${rest.winRate.toFixed(0)}% WR).`,
      action: 'Momentum days are rare — log the pre-market read that set this up so you can repeat it.',
      evidence: { first, rest },
      confidence: 0.75,
      priority: 50
    });
  }

  // Lost first, rest positive → resilient recovery
  if (!firstWon && restPnL > 0) {
    return makeObservation({
      id: `${key}:${day.date}:recovery`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.EMOTIONAL,
      title: 'Recovered from a losing open',
      detail: `First trade −$${Math.abs(first.pnl).toFixed(0)} but you added +$${restPnL.toFixed(0)} across the rest of the day.`,
      action: "Note what reset your state — that's the trait you want to practice.",
      evidence: { first, rest },
      confidence: 0.8,
      priority: 62
    });
  }

  // Won first, rest negative → gave it back (important warning)
  if (firstWon && restPnL < 0) {
    return makeObservation({
      id: `${key}:${day.date}:gaveback`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.EMOTIONAL,
      title: 'Gave back profits after winning open',
      detail: `First trade +$${first.pnl.toFixed(0)} but the next ${rest.trades} bled $${Math.abs(restPnL).toFixed(0)}.`,
      action: 'Consider a stop-trading threshold when up early. Protect the open.',
      evidence: { first, rest },
      confidence: 0.85,
      priority: 82
    });
  }

  // Lost first, rest also negative — compounded bad day
  if (!firstWon && restPnL < 0 && rest.trades >= 2) {
    return makeObservation({
      id: `${key}:${day.date}:spiral`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.EMOTIONAL,
      title: 'Lost first, kept losing',
      detail: `First trade −$${Math.abs(first.pnl).toFixed(0)}; the next ${rest.trades} added another −$${Math.abs(restPnL).toFixed(0)}.`,
      action: 'The opening loss may have shifted state. Next time, walk after trade #1 if it hits stop fast.',
      evidence: { first, rest },
      confidence: 0.8,
      priority: 80
    });
  }

  return null;
};
